import { z } from 'zod';
import { baseUserSchema } from './user';
import { specialtySchema } from './specialty';
import { siteSchema } from './site';
import { groupSchema } from './group';
import { provincesEnum } from './payor';

export const providerLicenseSchema = z.object({
  payorName: z.string(),
  payorID: z.uuid(),
  license: z.string(),
  siteID: z.guid(),
});

export const providerSchema = z.object({
  id: z.guid(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullable(),
  email: z.email().or(z.literal('')).nullable(),
  contactNote: z.string().nullable().optional(),
  province: provincesEnum.nullable().default('MB'),
  searchVector: z.string().optional().nullable(),
  license: z.string().nullable(),
  specialtyCode: z.string().nullable(),
  specialtyStartDate: z.string().nullable(),
  userID: z.uuid().nullable(),
  siteID: z.uuid().optional().nullable(),
  user: z.lazy(() => baseUserSchema).nullable(),
  sites: z.lazy(() => siteSchema.array()),
  specialty: z.lazy(() => specialtySchema).nullable(),
  licenses: z.array(providerLicenseSchema),
  groups: z.lazy(() => groupSchema.array()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable().optional(),
});

export const providerSummarySchema = providerSchema
  .pick({
    id: true,
    title: true,
    firstName: true,
    lastName: true,
  })
  .strict();

export const createProviderSchema = providerSchema
  .omit({
    searchVector: true,
    user: true,
    sites: true,
    license: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    province: provincesEnum.nullable().optional(),
    userID: z.uuid().nullable().optional(),
  });

export const updateProviderSchema = providerSchema.pick({
  id: true,
  firstName: true,
  lastName: true,
  title: true,
  email: true,
  contactNote: true,
  province: true,
  siteID: true,
  specialtyCode: true,
  specialtyStartDate: true,
  userID: true,
  specialty: true,
  licenses: true,
  groups: true,
});

export type CreateProviderInput = z.infer<typeof createProviderSchema>;

export type UpdateProviderInput = z.infer<typeof updateProviderSchema>;

export const isProvider = (obj: any): obj is z.infer<typeof providerSchema> => {
  return providerSchema.safeParse(obj).success;
};
